/**
 * Player (phone) shell. Subscribes to the room, calls `joinRoom` once on
 * mount, runs the heartbeat, and switches between the lobby UI and the
 * game module's PlayerGameView based on `room.status`.
 */

import { Box, Button, Loader, Paper, Stack, Text, Title } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getGameModule } from '../registry'
import { getDb } from '@/platform/firebase/client'
import { PlayerLobby } from '../lobby/PlayerLobby'
import { getOrCreateLocalUid, getStoredProfile } from '../lobby/profile'
import {
  RoomFullError,
  RoomLockedError,
  RoomNotFoundError,
  heartbeat,
  joinRoom,
  subscribeRoom,
  subscribeRoomState,
} from '../lobby/service'
import type { GameId, PlayerColor, Room } from '@mdev/common'
import type { GameModule } from '../module'

const db = getDb()

const HEARTBEAT_INTERVAL_MS = 10_000

type RoomFetchState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'ready'; room: Room<unknown> }

type JoinState =
  | { kind: 'pending' }
  | { kind: 'joined' }
  | { kind: 'error'; message: string }

const FALLBACK_COLOR_ORDER: PlayerColor[] = [ 'red', 'blue', 'green', 'purple', 'orange', 'teal' ]

export function PlayerShell() {
  const { gameId, code, uid: uidFromUrl } = useParams<{
    gameId: string
    code: string
    uid: string
  }>()

  const [ roomState, setRoomState ] = useState<RoomFetchState>( { kind: 'loading' } )
  const [ joinState, setJoinState ] = useState<JoinState>( { kind: 'pending' } )
  const [ gameStateData, setGameStateData ] = useState<unknown>( null )

  const gameModule: GameModule<unknown, unknown> | undefined = gameId
    ? getGameModule( gameId as GameId )
    : undefined

  // If this device's id is actually the room's host id (e.g. a player tab
  // opened in the host's own browser on a legacy room), it must NOT register
  // as a player — joinRoom already skips it, and heartbeating would create a
  // stray partial entry in the roster. Treat it as a non-player here.
  const isHostUid =
    roomState.kind === 'ready' && !!uidFromUrl && uidFromUrl === roomState.room.hostUid

  // Sanity-check uid against localStorage; warn if mismatched but trust the URL.
  useEffect( () => {
    if ( !uidFromUrl ) return
    const local = getOrCreateLocalUid()
    if ( local !== uidFromUrl ) {
      console.warn(
        '[PlayerShell] URL uid does not match localStorage uid.',
        { urlUid: uidFromUrl, localUid: local },
      )
    }
  }, [ uidFromUrl ] )

  // Subscribe to the room.
  useEffect( () => {
    if ( !code ) return
    const unsub = subscribeRoom<unknown>( code, ( room ) => {
      if ( room === null ) {
        setRoomState( { kind: 'not-found' } )
        return
      }
      setRoomState( { kind: 'ready', room } )
    } )
    return unsub
  }, [ code ] )

  // Join exactly once when we have everything we need.
  const joinAttemptedRef = useRef( false )
  useEffect( () => {
    if ( joinAttemptedRef.current ) return
    if ( !code || !uidFromUrl || !gameModule ) return
    if ( roomState.kind !== 'ready' ) return
    joinAttemptedRef.current = true

    const stored = getStoredProfile()
    // Pick a color the room hasn't claimed yet, falling back to red.
    const taken = new Set(
      Object.values( roomState.room.players )
        .filter( ( p ) => p.uid !== uidFromUrl )
        .map( ( p ) => p.color ),
    )
    const fallbackColor: PlayerColor =
      FALLBACK_COLOR_ORDER.find( ( c ) => !taken.has( c ) ) ?? 'red'

    const profile = stored ?? {
      displayName: 'Player',
      color: fallbackColor,
      avatar: '🦊',
    }

    void ( async () => {
      try {
        await joinRoom( code, uidFromUrl, profile, { maxPlayers: gameModule.maxPlayers } )
        setJoinState( { kind: 'joined' } )
      } catch ( err ) {
        let message = 'Could not join the room.'
        if ( err instanceof RoomNotFoundError ) message = 'Room not found.'
        else if ( err instanceof RoomFullError ) message = 'Room is full.'
        else if ( err instanceof RoomLockedError ) {
          message = 'Game already in progress and you weren\'t a player.'
        } else if ( err instanceof Error ) message = err.message
        setJoinState( { kind: 'error', message } )
      }
    } )()
  }, [ code, uidFromUrl, gameModule, roomState ] )

  // Heartbeat once we've joined. Never heartbeat as the host id — that would
  // write a partial player entry that pollutes the roster.
  useEffect( () => {
    if ( joinState.kind !== 'joined' ) return
    if ( !code || !uidFromUrl || isHostUid ) return
    void heartbeat( code, uidFromUrl ).catch( () => {} )
    const id = window.setInterval( () => {
      void heartbeat( code, uidFromUrl ).catch( () => {} )
    }, HEARTBEAT_INTERVAL_MS )
    return () => window.clearInterval( id )
  }, [ joinState.kind, code, uidFromUrl, isHostUid ] )

  // Subscribe to per-player merged game state while active OR finished —
  // finished games still need the state doc to render a per-game end-game
  // view (e.g. Scotland Yard's path reveal). Games that expose
  // `subscribePlayerState` (e.g. Scotland Yard, which merges in the private
  // Mr. X subdoc for the Mr. X player only) get their custom subscription
  // wired in here. Games without secrets fall back to a plain public-state
  // subscription so detective-style "everyone sees the same doc" games
  // remain trivial to add.
  const room = roomState.kind === 'ready' ? roomState.room : null
  const status = room?.status
  useEffect( () => {
    if ( !code || !uidFromUrl ) return
    if ( status !== 'active' && status !== 'finished' ) {
      setGameStateData( null )
      return
    }
    if ( gameModule?.subscribePlayerState ) {
      const unsub = gameModule.subscribePlayerState( db, code, uidFromUrl, ( s ) => {
        setGameStateData( s )
      } )
      return unsub
    }
    // Fallback: plain public-state subscription.
    const unsub = subscribeRoomState<unknown>( code, ( s ) => {
      setGameStateData( s )
    } )
    return unsub
  }, [ code, uidFromUrl, status, gameModule ] )

  const liveRoom: Room<unknown> | null = roomState.kind === 'ready' ? roomState.room : null

  // --- Error / loading states ---------------------------------------------

  if ( !gameId || !code || !uidFromUrl ) {
    return <ShellMessage title="Bad URL" body="Missing game id, room code, or player id." />
  }

  if ( !gameModule ) {
    return (
      <ShellMessage
        title="Unknown game"
        body={ `No game registered for "${ gameId }".` }
      />
    )
  }

  if ( roomState.kind === 'loading' ) {
    return <CenterLoader label={ `Connecting to ${ code }…` } />
  }

  if ( roomState.kind === 'not-found' ) {
    return <ShellMessage title="Room not found" body={ `No room with code "${ code }".` } />
  }

  if ( liveRoom && liveRoom.gameId !== gameModule.id ) {
    return (
      <ShellMessage
        title="Game mismatch"
        body={ `Room ${ code } is for ${ liveRoom.gameId }, not ${ gameModule.id }.` }
      />
    )
  }

  if ( joinState.kind === 'error' ) {
    return <ShellMessage title="Couldn't join" body={ joinState.message } />
  }

  if ( !liveRoom ) {
    return <CenterLoader label="Loading room…" />
  }

  if ( joinState.kind === 'pending' || !liveRoom.players[ uidFromUrl ] ) {
    return <CenterLoader label="Joining…" />
  }

  // --- Render based on room status ----------------------------------------

  if ( liveRoom.status === 'lobby' ) {
    return (
      <PlayerLobby
        room={ liveRoom }
        gameModule={ gameModule }
        uid={ uidFromUrl }
      />
    )
  }

  if ( liveRoom.status === 'active' ) {
    if ( gameStateData === null ) {
      return <CenterLoader label="Loading game…" />
    }
    const PlayerGameView = gameModule.PlayerGameView
    return (
      <PlayerGameView
        room={ liveRoom }
        state={ gameStateData }
        uid={ uidFromUrl }
      />
    )
  }

  // status === 'finished'
  // If the game module provides its own per-player end-game view, prefer it
  // (e.g. SY's PlayerFinishedView with the full Mr. X path reveal). Fall
  // back to the platform's generic finished panel otherwise. The custom
  // view depends on the state doc; if it hasn't arrived (or doesn't exist
  // for a legacy game), use the fallback.
  if ( gameModule.PlayerFinishedView && gameStateData !== null ) {
    const PlayerFinishedViewModule = gameModule.PlayerFinishedView
    return (
      <PlayerFinishedViewModule
        room={ liveRoom }
        state={ gameStateData }
        uid={ uidFromUrl }
      />
    )
  }
  return <PlayerFinishedView room={ liveRoom } />
}

function PlayerFinishedView( props: { room: Room<unknown> } ) {
  const winner = props.room.winner
  return (
    <Box
      style={ {
        minHeight: '100vh',
        background: '#0B0F1A',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } }
      p="md"
    >
      <Paper
        p="lg"
        withBorder
        style={ { borderColor: '#1F2937', background: '#0F172A' } }
      >
        <Stack
          gap="md"
          align="center"
        >
          <Title
            order={ 2 }
            c="white"
          >
            Game over
          </Title>
          { winner ? (
            <>
              <Text
                c="yellow"
                fw={ 700 }
                size="xl"
                tt="capitalize"
              >
                { winner.team } win!
              </Text>
              <Text c="dimmed" ta="center">{ winner.reason }</Text>
            </>
          ) : (
            <Text c="dimmed">No winner recorded.</Text>
          ) }
          <Button
            component={ Link }
            to="/games"
            variant="light"
          >
            Back to games
          </Button>
        </Stack>
      </Paper>
    </Box>
  )
}

function CenterLoader( props: { label: string } ) {
  return (
    <Box
      style={ {
        minHeight: '100vh',
        background: '#0B0F1A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } }
    >
      <Stack
        align="center"
        gap="md"
      >
        <Loader color="yellow" />
        <Text c="dimmed">{ props.label }</Text>
      </Stack>
    </Box>
  )
}

function ShellMessage( props: { title: string; body: string } ) {
  return (
    <Box
      style={ {
        minHeight: '100vh',
        background: '#0B0F1A',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } }
      p="md"
    >
      <Stack
        gap="md"
        align="center"
      >
        <Title order={ 2 } c="white">{ props.title }</Title>
        <Text c="dimmed" ta="center">{ props.body }</Text>
        <Button
          component={ Link }
          to="/games"
          variant="light"
        >
          Back to games
        </Button>
      </Stack>
    </Box>
  )
}
